import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import AudioEnginePill from "../components/AudioEnginePill";
import { toast } from "sonner";
import {
  Users,
  EnvelopeSimple,
  Phone,
  CurrencyDollar,
  Quotes,
  Calendar,
  MagnifyingGlass,
  CaretRight,
  PaperPlaneTilt,
} from "@phosphor-icons/react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export default function AdminUsers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [warmStatus, setWarmStatus] = useState(null);

  useEffect(() => {
    api
      .get("/admin/users")
      .then((r) => {
        setData(r.data);
        if (r.data?.users?.length) setSelected(r.data.users[0]);
      })
      .catch(() => toast.error("Failed to load users"))
      .finally(() => setLoading(false));
  }, []);

  // Poll warm-status every 5 sec while a warm job is running
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/admin/audio/warm/status");
        if (cancelled) return;
        setWarmStatus(r.data);
      } catch (_e) { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const startWarm = async (scope) => {
    try {
      const r = await api.post(`/admin/audio/warm?scope=${scope}`);
      if (r.data.status === "already-running") {
        toast.info("Warm job already running");
      } else {
        toast.success(`Warming ${scope} audios in background`);
      }
      setWarmStatus(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Warm failed");
    }
  };

  const filtered = useMemo(() => {
    const users = data?.users || [];
    if (!q.trim()) return users;
    const needle = q.toLowerCase();
    return users.filter(
      (u) =>
        (u.email || "").toLowerCase().includes(needle) ||
        (u.name || "").toLowerCase().includes(needle) ||
        (u.phone || "").includes(needle) ||
        (u.quiz_recommendation || "").toLowerCase().includes(needle),
    );
  }, [q, data]);

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10" data-testid="admin-users-page">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="eyebrow">
              <span className="gold-divider" />
              Admin · Master View
            </p>
            <h1 className="font-display text-4xl mt-3">Every User · Every Touchpoint</h1>
            <p className="text-sm text-exodus-muted mt-2 max-w-xl">
              Unified roster across leads, quiz responses, and Stripe reservations. One row per email.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <div className="flex gap-2">
              <button
                data-testid="warm-learn-btn"
                onClick={() => startWarm("learn")}
                disabled={warmStatus?.running}
                className="btn-ghost text-xs disabled:opacity-50"
              >
                Pre-warm Learn audios
              </button>
              <button
                data-testid="warm-all-btn"
                onClick={() => startWarm("all")}
                disabled={warmStatus?.running}
                className="btn-gold text-xs disabled:opacity-50"
              >
                Pre-warm ALL audios
              </button>
            </div>
            {warmStatus && (warmStatus.running || warmStatus.done > 0) && (
              <div className="text-[11px] text-exodus-muted text-right" data-testid="warm-status">
                {warmStatus.running ? "⏳ Generating " : "✓ Last run: "}
                <span className="text-exodus-gold">{warmStatus.done}/{warmStatus.total}</span>
                {warmStatus.failures > 0 && <span className="text-red-400 ml-2">{warmStatus.failures} failed</span>}
                {warmStatus.last && <div className="text-[10px] opacity-70 mt-0.5">{warmStatus.last}</div>}
              </div>
            )}
            <OfflineAudioCacheStat />
          </div>
        </div>

        {/* AUDIO ENGINE COCKPIT */}
        <div className="mb-8">
          <AudioEnginePill />
        </div>

        {/* KPI strip */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="admin-kpis">
            <Kpi label="Unique users" value={data.total_users} icon={Users} />
            <Kpi label="Leads" value={data.total_leads} icon={EnvelopeSimple} />
            <Kpi label="Quiz takers" value={data.total_quizzes} icon={Quotes} />
            <Kpi label="Reservations" value={data.paid_reservations + "/" + data.total_reservations} icon={Calendar} />
            <Kpi label="Revenue (USD)" value={"$" + data.revenue_usd.toLocaleString()} icon={CurrencyDollar} />
          </div>
        )}

        {/* Search */}
        <div className="card-surface flex items-center gap-3 px-4 py-2 mb-6">
          <MagnifyingGlass size={16} className="text-exodus-muted" />
          <input
            data-testid="admin-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email, name, phone, or recommendation…"
            className="bg-transparent flex-1 text-sm focus:outline-none placeholder:text-exodus-muted/70"
          />
        </div>

        {loading ? (
          <p className="text-exodus-muted">Loading users…</p>
        ) : filtered.length === 0 ? (
          <div className="card-surface p-10 text-center">
            <Users size={32} weight="thin" className="mx-auto text-exodus-gold mb-4" />
            <h3 className="font-display text-2xl">No users match.</h3>
            <p className="text-sm text-exodus-muted mt-2">Clear the search to see everyone.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-6">
            {/* LEFT — list */}
            <div className="lg:col-span-5 card-surface divide-y divide-exodus-border max-h-[70vh] overflow-auto">
              {filtered.map((u) => (
                <button
                  key={u.email}
                  data-testid={`admin-user-${u.email}`}
                  onClick={() => setSelected(u)}
                  className={`w-full px-5 py-4 flex items-center gap-3 text-left transition-colors ${
                    selected?.email === u.email
                      ? "bg-exodus-elevated border-l-2 border-exodus-gold"
                      : "hover:bg-exodus-elevated"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-base truncate">{u.name || u.email}</p>
                    <p className="text-xs text-exodus-muted truncate">{u.email}</p>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      {u.stage && (
                        <span className="text-[10px] uppercase tracking-wider gold-text">{u.stage}</span>
                      )}
                      {u.quiz_count > 0 && (
                        <span className="text-[10px] uppercase tracking-wider text-exodus-muted">
                          · {u.quiz_count} quiz
                        </span>
                      )}
                      {u.reservation_paid && (
                        <span className="text-[10px] uppercase tracking-wider text-emerald-400">· PAID</span>
                      )}
                    </div>
                  </div>
                  <CaretRight size={14} className="text-exodus-muted shrink-0" />
                </button>
              ))}
            </div>

            {/* RIGHT — detail */}
            <aside className="lg:col-span-7 lg:sticky lg:top-6 self-start space-y-4">
              {selected ? (
                <div className="card-surface p-6" data-testid="admin-user-detail">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="eyebrow text-xs">User detail</p>
                      <h2 className="font-display text-3xl mt-1">{selected.name || "—"}</h2>
                    </div>
                    <button
                      data-testid={`reactivate-${selected.email}`}
                      onClick={async () => {
                        if (!window.confirm(`Send a reactivation email to ${selected.email}?`)) return;
                        try {
                          const r = await api.post(
                            `/admin/users/${encodeURIComponent(selected.email)}/reactivate`,
                          );
                          toast.success(`Sent: ${r.data.subject}`);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Send failed");
                        }
                      }}
                      className="btn-ghost text-xs"
                    >
                      <PaperPlaneTilt size={14} weight="bold" /> Reactivate
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                    <Field icon={EnvelopeSimple} label="Email" value={selected.email} />
                    <Field icon={Phone} label="Phone" value={selected.phone || "—"} />
                    <Field icon={Calendar} label="First seen" value={fmtDate(selected.first_seen)} />
                    <Field icon={Calendar} label="Last activity" value={fmtDate(selected.last_activity)} />
                    <Field icon={Quotes} label="Quiz recommendation" value={selected.quiz_recommendation || "—"} />
                    <Field
                      icon={CurrencyDollar}
                      label="Total paid"
                      value={"$" + (selected.total_paid_usd || 0).toLocaleString()}
                    />
                    <Field icon={Users} label="Pipeline stage" value={selected.stage || "—"} />
                    <Field icon={Users} label="Source" value={selected.source || "—"} />
                  </div>
                  {selected.notes && (
                    <div className="mt-5 p-4 bg-exodus-elevated border border-exodus-border">
                      <p className="eyebrow text-xs mb-1">Internal notes</p>
                      <p className="text-sm text-exodus-text/90 whitespace-pre-wrap">{selected.notes}</p>
                    </div>
                  )}

                  <div className="mt-6">
                    <p className="eyebrow text-xs mb-2">Activity timeline</p>
                    <ul className="space-y-2 max-h-64 overflow-auto pr-2">
                      {selected.events.map((e, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-3 text-xs border-l-2 border-exodus-border pl-3 py-1"
                        >
                          <span className="font-mono text-exodus-muted shrink-0">{fmtDate(e.at)}</span>
                          <span className="uppercase tracking-wider gold-text shrink-0">{e.type}</span>
                          <span className="text-exodus-text/80">{e.detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="card-surface p-10 text-center text-exodus-muted text-sm">
                  Select a user to see details.
                </div>
              )}
            </aside>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Kpi({ label, value, icon: Icon }) {
  return (
    <div className="card-surface p-4 flex items-center gap-3" data-testid={`admin-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon size={20} className="text-exodus-gold shrink-0" weight="thin" />
      <div className="min-w-0">
        <p className="text-[10px] eyebrow text-exodus-muted">{label}</p>
        <p className="font-display text-xl truncate">{value}</p>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div>
      <p className="text-[10px] eyebrow text-exodus-muted flex items-center gap-1">
        <Icon size={11} /> {label}
      </p>
      <p className="text-sm truncate">{value}</p>
    </div>
  );
}

/**
 * OfflineAudioCacheStat — shows how many audios are cached client-side by the
 * service worker, plus a button to clear the local cache. Talks to /audio-sw.js
 * via postMessage.
 */
function OfflineAudioCacheStat() {
  const [count, setCount] = React.useState(null);

  const refresh = React.useCallback(() => {
    if (!("serviceWorker" in navigator) || !navigator.serviceWorker.controller) {
      setCount("unavailable");
      return;
    }
    const chan = new MessageChannel();
    chan.port1.onmessage = (e) => {
      if (e.data?.type === "audio-cache-stats") setCount(e.data.count);
    };
    navigator.serviceWorker.controller.postMessage("audio-cache-stats", [chan.port2]);
  }, []);

  React.useEffect(() => {
    refresh();
    const id = setInterval(refresh, 10000);
    return () => clearInterval(id);
  }, [refresh]);

  const clear = () => {
    if (!navigator.serviceWorker?.controller) return;
    if (!window.confirm("Clear all cached audios on this device? Users will re-download on next play.")) return;
    navigator.serviceWorker.controller.postMessage("clear-audio-cache");
    setTimeout(refresh, 500);
  };

  if (count === "unavailable") {
    return (
      <div className="text-[10px] text-exodus-muted text-right mt-1">
        Offline audio cache: not registered yet — reload page after first visit.
      </div>
    );
  }
  return (
    <div className="text-[10px] text-exodus-muted text-right mt-1">
      Offline audio cache: <span className="text-exodus-gold">{count ?? "…"}</span> audios stored locally ·
      <button
        onClick={clear}
        className="ml-2 underline hover:text-exodus-text"
        data-testid="clear-audio-cache-btn"
      >
        Clear
      </button>
    </div>
  );
}
